import { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import {
  BOOKING_FEEDBACK_FORM_KINDS,
  type BookingFeedbackSpace,
} from '@mds/shared/types/booking-feedback';

import { useSession } from '@/auth/use-session';
import * as feedback from '@/bookings/booking-feedback.api';
import BookingFeedbackBuilder, {
  type FeedbackFormTarget,
} from '@/components/booking/booking-feedback-builder';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Modal from '@/components/ui/modal';
import Switch from '@/components/ui/switch';
import { Cell, Row, Table } from '@/components/ui/table';
import { FORM_KIND_LABELS } from '@/domain/booking-feedback';
import { confirmLeave } from '@/hooks/use-unsaved';

const SPACES_KEY = ['booking-feedback-spaces'];

const titleOf = (target: FeedbackFormTarget) =>
  'space' in target ? `${target.space.name} form` : `${FORM_KIND_LABELS[target.kind]} default form`;

/**
 * Which spaces ask for feedback, and which form each asks. Every space has its own switch, and
 * asks its kind's default form unless it has one of its own. Forms open in a dialog.
 */
export default function BookingFeedbackSpaces() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('reservations.manage');
  const spaces = useQuery({ queryKey: SPACES_KEY, queryFn: feedback.listSpaces });

  const [editing, setEditing] = useState<FeedbackFormTarget | null>(null);
  const [formActions, setFormActions] = useState<HTMLDivElement | null>(null);
  /** The space whose own form is about to be removed. */
  const [resetting, setResetting] = useState<BookingFeedbackSpace | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const failed = (error: unknown) =>
    setProblem(error instanceof ApiError ? error.message : 'That did not save. Try again.');

  const toggle = async (space: BookingFeedbackSpace, isEnabled: boolean) => {
    setBusyId(space.id);
    setProblem(null);
    try {
      const updated = await feedback.updateSpace(space.id, { isEnabled });
      queryClient.setQueryData<BookingFeedbackSpace[]>(SPACES_KEY, (old) =>
        old?.map((s) => (s.id === updated.id ? updated : s)),
      );
    } catch (error) {
      failed(error);
    } finally {
      setBusyId(null);
    }
  };

  const revertToDefault = async (space: BookingFeedbackSpace) => {
    setBusyId(space.id);
    setProblem(null);
    try {
      await feedback.removeSpaceForm(space.id);
      queryClient.removeQueries({ queryKey: ['booking-feedback-form', 'space', space.id] });
      await queryClient.invalidateQueries({ queryKey: SPACES_KEY });
      setResetting(null);
    } catch (error) {
      failed(error);
    } finally {
      setBusyId(null);
    }
  };

  if (spaces.isPending) return <p className="m-0 text-base text-ink-muted">Loading spaces…</p>;
  if (spaces.isError)
    return (
      <EmptyState
        title="Could not load the spaces"
        hint={
          spaces.error instanceof ApiError
            ? spaces.error.message
            : 'The server could not be reached.'
        }
      />
    );

  const all = spaces.data;
  const usingDefault = (kind: BookingFeedbackSpace['kind']) =>
    all.filter((s) => s.kind === kind && !s.hasOwnForm).length;

  return (
    <div className="flex flex-col gap-6">
      {problem && (
        <p
          role="alert"
          className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
        >
          {problem}
        </p>
      )}

      <Card title="Default forms">
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {BOOKING_FEEDBACK_FORM_KINDS.map((kind) => (
            <li
              key={kind}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hairline bg-raised px-4 py-3"
            >
              <span className="flex flex-col">
                <span className="text-base font-semibold">{FORM_KIND_LABELS[kind]}</span>
                <span className="text-sm text-ink-muted">
                  Asked by {usingDefault(kind)} {usingDefault(kind) === 1 ? 'space' : 'spaces'}{' '}
                  without a form of their own
                </span>
              </span>
              <Button variant="quiet" onClick={() => setEditing({ kind })}>
                {mayManage ? 'Edit form' : 'View form'}
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Spaces">
        <p className="m-0 mb-4 text-base text-ink-secondary">
          A space asks for feedback only while it is switched on, and only about bookings that end
          after that. Give a space its own form when its questions should differ from its default.
        </p>
        <Table head={['Space', 'Asks', 'Feedback', '']}>
          {all.map((space) => (
            <Row key={space.id}>
              <Cell>
                <span className="flex flex-wrap items-center gap-2 font-semibold">
                  {space.name}
                  {!space.isActive && <Chip>Inactive</Chip>}
                </span>
              </Cell>
              <Cell>
                {space.hasOwnForm ? (
                  <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
                    Own form
                  </Chip>
                ) : (
                  <span className="text-ink-secondary">{FORM_KIND_LABELS[space.kind]} default</span>
                )}
              </Cell>
              <Cell>
                <span className="flex flex-col">
                  <Switch
                    checked={space.isEnabled}
                    disabled={!mayManage || busyId === space.id}
                    label={space.isEnabled ? 'On' : 'Off'}
                    onChange={(next) => void toggle(space, next)}
                  />
                  {space.isEnabled && (
                    <span className="text-sm text-ink-muted">Since {stamp(space.enabledAt)}</span>
                  )}
                </span>
              </Cell>
              <Cell className="text-right">
                <span className="inline-flex flex-wrap justify-end gap-2">
                  {space.hasOwnForm ? (
                    <>
                      <Button variant="quiet" onClick={() => setEditing({ space })}>
                        {mayManage ? 'Edit form' : 'View form'}
                      </Button>
                      {mayManage && (
                        <Button
                          variant="quiet"
                          disabled={busyId === space.id}
                          onClick={() => setResetting(space)}
                        >
                          Use default
                        </Button>
                      )}
                    </>
                  ) : (
                    mayManage && (
                      <Button variant="quiet" onClick={() => setEditing({ space })}>
                        Give it its own form
                      </Button>
                    )
                  )}
                </span>
              </Cell>
            </Row>
          ))}
        </Table>
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => {
          if (confirmLeave()) setEditing(null);
        }}
        title={editing ? titleOf(editing) : 'Feedback form'}
        footer={<div ref={setFormActions} className="contents" />}
      >
        {editing && (
          <BookingFeedbackBuilder
            key={'space' in editing ? editing.space.id : editing.kind}
            target={editing}
            actionsSlot={formActions}
          />
        )}
      </Modal>

      <Modal
        open={resetting !== null}
        onClose={() => setResetting(null)}
        title="Use the default form"
        footer={
          resetting && (
            <>
              <Button
                variant="danger"
                disabled={busyId === resetting.id}
                onClick={() => void revertToDefault(resetting)}
              >
                {busyId === resetting.id ? 'Removing…' : 'Remove its own form'}
              </Button>
              <Button variant="quiet" onClick={() => setResetting(null)}>
                Keep it
              </Button>
            </>
          )
        }
      >
        {resetting && (
          <p className="m-0 text-base text-ink-secondary">
            {resetting.name} will ask the {FORM_KIND_LABELS[resetting.kind].toLowerCase()} default
            form again, and its own questions are removed. Answers already given keep their
            questions.
          </p>
        )}
      </Modal>
    </div>
  );
}
